import { Box, Button, FormHelperText, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { CATALOGUE_IMAGE_MAX_BYTES, CATALOGUE_IMAGE_TYPES } from '@app/shared';
import type { CatalogueImageRef } from '@app/shared';
import { api } from '../../lib/api';

/**
 * Pictures of the catalogue: Image 1 to 3 of a model, Image 1 of an equipment (release switch
 * `catalogue-images`). A form keeps the changes in a draft and saves them after the record,
 * so a new record gets its pictures once it has an id.
 */
export const CATALOGUE_IMAGES_FEATURE = 'catalogue-images';

/** A chosen file, or 'remove' to take the picture away. */
export type ImageDraft = Record<number, File | 'remove'>;

export const modelImagePath = (id: string, slot: number) => `/product-models/${id}/images/${slot}`;
export const equipmentImagePath = (id: string) => `/equipment/${id}/image`;

/** Address of a stored picture; the date makes a browser fetch a replaced one again. */
export const imageUrl = (path: string, image: CatalogueImageRef) =>
  api.url(path, { v: image.updatedAt });

/** The reference's limits: jpg, jpeg, png or gif, at most 2 MB. Empty when the file is fine. */
export function imageProblem(file: File): string {
  if (!(CATALOGUE_IMAGE_TYPES as readonly string[]).includes(file.type))
    return 'The image must be a jpg, jpeg, png or gif file.';
  if (file.size > CATALOGUE_IMAGE_MAX_BYTES) return 'The image must be 2 MB or smaller.';
  return '';
}

/** Uploads and removals of a draft, slot by slot, after the record was saved. */
export async function saveImageDraft(
  draft: ImageDraft,
  pathOf: (slot: number) => string,
): Promise<void> {
  for (const [slot, change] of Object.entries(draft)) {
    const path = pathOf(Number(slot));
    if (change === 'remove') await api.delete(path);
    else {
      const form = new FormData();
      form.append('file', change);
      await api.upload(path, form);
    }
  }
}

export function Thumbnail({ src, alt, size = 40 }: { src: string; alt: string; size?: number }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ width: size, height: size, objectFit: 'contain', display: 'block' }}
    />
  );
}

function Slot({
  label,
  current,
  change,
  onChange,
}: {
  label: string;
  current: string | null;
  change: File | 'remove' | undefined;
  onChange: (change: File | 'remove' | undefined) => void;
}) {
  const [problem, setProblem] = useState('');
  const preview = useMemo(
    () => (change instanceof File ? URL.createObjectURL(change) : null),
    [change],
  );
  useEffect(() => () => (preview ? URL.revokeObjectURL(preview) : undefined), [preview]);
  const shown = change === 'remove' ? null : (preview ?? current);
  const inputId = `catalogue-image-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <Box role="group" aria-label={label} sx={{ display: 'grid', gap: 0.5, justifyItems: 'start' }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: 96,
          height: 96,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {shown ? (
          <Thumbnail src={shown} alt={label} size={90} />
        ) : (
          <Typography variant="caption" color="text.secondary">
            No image
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Button component="label" size="small" htmlFor={inputId}>
          Choose file
          <input
            id={inputId}
            hidden
            type="file"
            accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
            aria-label={`${label} file`}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (!file) return;
              const why = imageProblem(file);
              setProblem(why);
              if (!why) onChange(file);
            }}
          />
        </Button>
        {shown ? (
          <Button
            size="small"
            color="error"
            onClick={() => {
              setProblem('');
              onChange(current ? 'remove' : undefined);
            }}
          >
            Remove
          </Button>
        ) : null}
      </Box>
      <FormHelperText error={Boolean(problem)}>
        {problem || 'jpg, jpeg, png or gif; max 2 MB'}
      </FormHelperText>
    </Box>
  );
}

/** The image slots of a form, each with its picture, "Choose file" and "Remove". */
export function ImageSlots({
  slots,
  draft,
  onChange,
}: {
  slots: ReadonlyArray<{ slot: number; label: string; url: string | null }>;
  draft: ImageDraft;
  onChange: (draft: ImageDraft) => void;
}) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {slots.map((s) => (
        <Slot
          key={s.slot}
          label={s.label}
          current={s.url}
          change={draft[s.slot]}
          onChange={(change) => {
            const next = { ...draft };
            if (change === undefined) delete next[s.slot];
            else next[s.slot] = change;
            onChange(next);
          }}
        />
      ))}
    </Box>
  );
}
